import { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "api/client";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// MUI
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import InputAdornment from "@mui/material/InputAdornment";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";

const PROFILE_KEY = "solvit_profile";
const TOKEN_KEY = "solvit_auth_token";

function resolveMediaUrl(url) {
  if (!url) return "";

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  if (url.startsWith("http://") || url.startsWith("https://")) {
    if (url.includes("localhost") || url.includes("127.0.0.1")) {
      try {
        const u = new URL(url);
        return `${origin}/solvit-api${u.pathname.startsWith("/") ? "" : "/"}${u.pathname}`;
      } catch {
        return url;
      }
    }
    return url;
  }

  const normalized = url.startsWith("/") ? url : `/${url}`;

  if (normalized.startsWith("/solvit-api/")) return `${origin}${normalized}`;
  if (normalized.startsWith("/api/")) return `${origin}/solvit-api${normalized}`;
  if (normalized.startsWith("/uploads/")) return `${origin}/solvit-api${normalized}`;

  return `${origin}/solvit-api${normalized}`;
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "-";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function normalizeTags(item) {
  if (!item) return [];
  if (Array.isArray(item.tags)) return item.tags.map((t) => String(t || "").trim()).filter(Boolean);
  if (typeof item.tags === "string") {
    return item.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

function normalizeFolder(value) {
  if (typeof value !== "string") return "";
  return value.trim().replace(/^\/+|\/+$/g, "");
}

export default function Media() {
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [customerNumber, setCustomerNumber] = useState(null);
  const [allMedia, setAllMedia] = useState([]);

  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [search, setSearch] = useState("");
  const [folderFilter, setFolderFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFilename, setEditFilename] = useState("");
  const [editFolder, setEditFolder] = useState("");
  const [editTags, setEditTags] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  const [replacingItem, setReplacingItem] = useState(null);
  const [replaceBusyId, setReplaceBusyId] = useState("");

  const isAdmin = String(profile?.role || "").toUpperCase() === "ADMIN";

  const refreshMedia = async (token) => {
    const mediaRes = await apiFetch("/media", { token });
    setAllMedia(Array.isArray(mediaRes) ? mediaRes : mediaRes?.media || mediaRes?.items || []);
  };

  useEffect(() => {
    const load = async () => {
      const profileRaw = localStorage.getItem(PROFILE_KEY);
      const p = profileRaw ? safeParse(profileRaw) : null;
      setProfile(p);
      setCustomerNumber(p?.customerNumber || null);

      try {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) {
          setError("Kein Login-Token gefunden. Bitte neu einloggen.");
          setAllMedia([]);
          return;
        }
        await refreshMedia(token);
      } catch (e) {
        console.error(e);
        setError(e?.message || "Medien konnten nicht geladen werden");
        setAllMedia([]);
      }
    };

    load();
  }, []);

  const checksumCounts = useMemo(() => {
    const map = new Map();
    allMedia.forEach((item) => {
      const checksum = item?.checksumSha256;
      if (!checksum) return;
      map.set(checksum, (map.get(checksum) || 0) + 1);
    });
    return map;
  }, [allMedia]);

  const allFolders = useMemo(() => {
    const set = new Set();
    allMedia.forEach((item) => {
      const folder = normalizeFolder(item.folder || "");
      if (folder) set.add(folder);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allMedia]);

  const allTags = useMemo(() => {
    const set = new Set();
    allMedia.forEach((item) => {
      normalizeTags(item).forEach((tag) => set.add(tag));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allMedia]);

  const visibleMedia = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allMedia.filter((item) => {
      const tags = normalizeTags(item);
      const folder = normalizeFolder(item.folder || "");
      const duplicateCount = Number(item.duplicateCount || checksumCounts.get(item.checksumSha256) || 1);

      if (q) {
        const haystack = [item.filename, folder, ...tags].join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      if (folderFilter && folder.toLowerCase() !== folderFilter.toLowerCase()) return false;
      if (tagFilter && !tags.some((tag) => tag.toLowerCase() === tagFilter.toLowerCase())) return false;
      if (duplicatesOnly && duplicateCount < 2) return false;

      return true;
    });
  }, [allMedia, search, folderFilter, tagFilter, duplicatesOnly, checksumCounts]);

  const duplicateMediaCount = useMemo(
    () => allMedia.filter((item) => Number(item.duplicateCount || checksumCounts.get(item.checksumSha256) || 1) > 1).length,
    [allMedia, checksumCounts]
  );

  const handlePickFiles = () => {
    setError("");
    setSuccess("");
    fileInputRef.current?.click();
  };

  const handleFilesSelected = async (e) => {
    setError("");
    setSuccess("");
    const files = Array.from(e.target.files || []);
    e.target.value = "";

    if (files.length === 0) return;

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setError("Kein Login-Token gefunden. Bitte neu einloggen.");
      return;
    }

    if (isAdmin) {
      setError("Admin-Upload bitte in Admin > Medien mit Kundenauswahl durchführen.");
      return;
    }

    setIsUploading(true);
    try {
      for (const file of files) {
        const isImage = file.type.startsWith("image/");
        const isVideo = file.type.startsWith("video/");
        if (!isImage && !isVideo) continue;

        const form = new FormData();
        form.append("file", file);
        if (folderFilter) form.append("folder", folderFilter);
        if (tagFilter) form.append("tags", JSON.stringify([tagFilter]));

        await apiFetch("/media/upload", {
          token,
          method: "POST",
          body: form,
          headers: {},
        });
      }

      await refreshMedia(token);
      setSuccess("Upload erfolgreich.");
    } catch (err) {
      console.error(err);
      setError(err?.message || "Upload fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setIsUploading(false);
    }
  };

  const requestDelete = (item) => {
    setPendingDelete(item);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setError("Kein Login-Token gefunden. Bitte neu einloggen.");
      return;
    }

    try {
      await apiFetch(`/media/${pendingDelete.id}`, {
        token,
        method: "DELETE",
      });

      setAllMedia((prev) => prev.filter((m) => m.id !== pendingDelete.id));
      setDeleteOpen(false);
      setPendingDelete(null);
      setSuccess("Datei gelöscht.");
    } catch (e) {
      console.error(e);
      setError(e?.message || "Löschen fehlgeschlagen");
    }
  };

  const cancelDelete = () => {
    setDeleteOpen(false);
    setPendingDelete(null);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setEditFilename(item?.filename || "");
    setEditFolder(item?.folder || "");
    setEditTags(normalizeTags(item).join(", "));
    setEditOpen(true);
  };

  const closeEditDialog = () => {
    setEditOpen(false);
    setEditingItem(null);
    setEditFilename("");
    setEditFolder("");
    setEditTags("");
    setEditBusy(false);
  };

  const saveMetadata = async () => {
    if (!editingItem) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setError("Kein Login-Token gefunden. Bitte neu einloggen.");
      return;
    }

    setEditBusy(true);
    setError("");
    setSuccess("");

    try {
      const tags = editTags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);

      await apiFetch(`/media/${editingItem.id}`, {
        token,
        method: "PATCH",
        body: JSON.stringify({
          filename: editFilename,
          folder: normalizeFolder(editFolder),
          tags,
        }),
      });

      await refreshMedia(token);
      closeEditDialog();
      setSuccess("Metadaten gespeichert.");
    } catch (e) {
      console.error(e);
      setError(e?.message || "Speichern fehlgeschlagen");
    } finally {
      setEditBusy(false);
    }
  };

  const pickReplaceFile = (item) => {
    setReplacingItem(item);
    replaceInputRef.current?.click();
  };

  const handleReplaceSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file || !replacingItem) return;

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setError("Kein Login-Token gefunden. Bitte neu einloggen.");
      return;
    }

    setError("");
    setSuccess("");
    setReplaceBusyId(replacingItem.id);

    try {
      const form = new FormData();
      form.append("file", file);

      const result = await apiFetch(`/media/${replacingItem.id}/replace`, {
        token,
        method: "POST",
        body: form,
        headers: {},
      });

      await refreshMedia(token);
      setSuccess(`Datei überall ersetzt (${Number(result?.replacedInLoops || 0)} Loop-Einträge aktualisiert).`);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Ersetzen fehlgeschlagen");
    } finally {
      setReplaceBusyId("");
      setReplacingItem(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="medium" mb={0.5}>
              Mediathek
            </MDTypography>
            <MDTypography variant="button" color="text">
              Organisiere deine Medien mit Ordnern und Tags, finde Duplikate und ersetze Dateien in allen Loops.
            </MDTypography>
            {!customerNumber && (
              <MDTypography variant="caption" color="error">
                Keine Kundennummer gefunden. Bitte im Profil hinterlegen.
              </MDTypography>
            )}
          </MDBox>

          <MDBox display="flex" gap={1}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              style={{ display: "none" }}
              onChange={handleFilesSelected}
            />
            <input
              ref={replaceInputRef}
              type="file"
              accept="image/*,video/*"
              style={{ display: "none" }}
              onChange={handleReplaceSelected}
            />
            <MDButton
              variant="gradient"
              color="info"
              startIcon={<Icon>upload</Icon>}
              disabled={isUploading}
              onClick={handlePickFiles}
            >
              {isUploading ? "Upload..." : "Medien hochladen"}
            </MDButton>
          </MDBox>
        </MDBox>

        {(error || success) && (
          <MDBox mb={2}>
            {!!error && (
              <MDTypography variant="caption" color="error" display="block">
                {error}
              </MDTypography>
            )}
            {!!success && (
              <MDTypography variant="caption" color="success" display="block">
                {success}
              </MDTypography>
            )}
          </MDBox>
        )}

        <Card sx={{ mb: 2 }}>
          <MDBox p={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Suche"
                  placeholder="Dateiname, Ordner oder Tag"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon fontSize="small">search</Icon>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Ordner"
                  value={folderFilter}
                  onChange={(e) => setFolderFilter(e.target.value)}
                >
                  <MenuItem value="">Alle</MenuItem>
                  {allFolders.map((folder) => (
                    <MenuItem key={folder} value={folder}>
                      {folder}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  select
                  label="Tag"
                  value={tagFilter}
                  onChange={(e) => setTagFilter(e.target.value)}
                >
                  <MenuItem value="">Alle</MenuItem>
                  {allTags.map((tag) => (
                    <MenuItem key={tag} value={tag}>
                      {tag}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControlLabel
                  control={<Switch checked={duplicatesOnly} onChange={(e) => setDuplicatesOnly(e.target.checked)} />}
                  label="Nur Duplikate"
                />
              </Grid>
            </Grid>

            <MDBox mt={1} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <MDTypography variant="button" color="text">
                {visibleMedia.length} Datei{visibleMedia.length === 1 ? "" : "en"}
              </MDTypography>
              <MDTypography variant="caption" color={duplicateMediaCount > 0 ? "warning" : "text"}>
                {duplicateMediaCount} Duplikat-Datei{duplicateMediaCount === 1 ? "" : "en"} erkannt
              </MDTypography>
            </MDBox>
          </MDBox>
        </Card>

        <Grid container spacing={3}>
          {visibleMedia.map((item) => {
            const isImage = item.mimeType?.startsWith("image/");
            const isVideo = item.mimeType?.startsWith("video/");
            const tags = normalizeTags(item);
            const duplicateCount = Number(item.duplicateCount || checksumCounts.get(item.checksumSha256) || 1);
            const usageCount = Number(item.usageCount || item?._count?.loopItems || 0);

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                <Card sx={{ border: duplicateCount > 1 ? "1px solid #f39c12" : "1px solid transparent" }}>
                  <MDBox p={2}>
                    <MDBox
                      borderRadius="lg"
                      overflow="hidden"
                      height="160px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      bgcolor="grey.100"
                    >
                      {isImage && (
                        <img
                          src={resolveMediaUrl(item.publicUrl || item.url || item.dataUrl)}
                          alt={item.filename || item.originalName || item.name || "media"}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      )}
                      {isVideo && (
                        <video
                          src={resolveMediaUrl(item.publicUrl || item.url || item.dataUrl)}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          controls
                          muted
                        />
                      )}
                      {!isImage && !isVideo && (
                        <MDTypography variant="caption" color="text">
                          Vorschau nicht verfügbar
                        </MDTypography>
                      )}
                    </MDBox>

                    <MDBox mt={1.5}>
                      <MDTypography
                        variant="button"
                        fontWeight="medium"
                        title={item.filename || item.originalName || item.name}
                        sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {item.filename || item.originalName || item.name}
                      </MDTypography>

                      <MDTypography variant="caption" color="text" display="block">
                        {(isImage ? "Bild" : isVideo ? "Video" : item.mimeType || "Datei")} • {formatBytes(item.sizeBytes ?? item.size)}
                      </MDTypography>

                      <MDTypography variant="caption" color="text" display="block">
                        Verwendet in {usageCount} Loop{usageCount === 1 ? "" : "s"}
                      </MDTypography>

                      <MDTypography variant="caption" color="text" display="block">
                        Ordner: {item.folder || "-"}
                      </MDTypography>

                      {duplicateCount > 1 && (
                        <MDTypography variant="caption" color="warning" display="block">
                          Duplikat erkannt ({duplicateCount}x)
                        </MDTypography>
                      )}

                      <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.8} useFlexGap>
                        {tags.map((tag) => (
                          <Chip key={`${item.id}-${tag}`} label={tag} size="small" />
                        ))}
                      </Stack>
                    </MDBox>

                    <MDBox mt={1.5} display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox display="flex" gap={0.5}>
                        <Tooltip title="Metadaten bearbeiten">
                          <IconButton size="small" onClick={() => openEditDialog(item)}>
                            <Icon fontSize="small">edit</Icon>
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Datei überall ersetzen">
                          <span>
                            <IconButton
                              size="small"
                              color="info"
                              disabled={replaceBusyId === item.id}
                              onClick={() => pickReplaceFile(item)}
                            >
                              <Icon fontSize="small">published_with_changes</Icon>
                            </IconButton>
                          </span>
                        </Tooltip>
                      </MDBox>

                      <Tooltip title="Löschen">
                        <IconButton size="small" color="error" onClick={() => requestDelete(item)}>
                          <Icon fontSize="small">delete</Icon>
                        </IconButton>
                      </Tooltip>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            );
          })}

          {visibleMedia.length === 0 && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                    Keine Treffer
                  </MDTypography>
                  <MDTypography variant="button" color="text" mb={2}>
                    Passe Filter an oder lade neue Medien hoch.
                  </MDTypography>
                  <MDButton variant="gradient" color="info" startIcon={<Icon>upload</Icon>} onClick={handlePickFiles}>
                    Medien hochladen
                  </MDButton>
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>

      <Footer />

      <Dialog open={deleteOpen} onClose={cancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Medium löschen?</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Möchtest du <strong>{pendingDelete?.filename || pendingDelete?.name}</strong> wirklich löschen?
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <MDButton variant="text" color="secondary" onClick={cancelDelete}>
            Abbrechen
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={confirmDelete}>
            Löschen
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={closeEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Medium bearbeiten</DialogTitle>
        <DialogContent>
          <MDBox pt={1} display="grid" gap={2}>
            <TextField
              fullWidth
              label="Dateiname"
              value={editFilename}
              onChange={(e) => setEditFilename(e.target.value)}
            />
            <TextField
              fullWidth
              label="Ordner"
              placeholder="z.B. kampagnen/sommer"
              value={editFolder}
              onChange={(e) => setEditFolder(e.target.value)}
            />
            <TextField
              fullWidth
              label="Tags"
              placeholder="z.B. promo, saison, social"
              value={editTags}
              onChange={(e) => setEditTags(e.target.value)}
              helperText="Kommagetrennt"
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <MDButton variant="text" color="secondary" onClick={closeEditDialog}>
            Abbrechen
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={saveMetadata} disabled={editBusy}>
            {editBusy ? "Speichert..." : "Speichern"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
